import React, { useMemo, useState } from "react";
import { monitoringClient } from "src/services/requestClient";

const AnalysisOverviewContext = React.createContext({});

const AnalysisOverviewProvider = ({ children }) => {
  const [marketOverview, setMarketOverview] = useState({});
  const [marketOverviewLoading, setMarketOverviewLoading] = useState(false);

  const [tokenDomination, setTokenDomination] = useState({});
  const [tokenDominationLoading, setTokenDominationLoading] = useState(false);

  const [utilizationRate, setUtilizationRate] = useState({});
  const [utilizationRateLoading, setUtilizationRateLoading] = useState(false);

  const [interestRate, setInterestRate] = useState({});
  const [interestRateLoading, setInterestRateLoading] = useState(false);

  const [numberOfTransactions, setNumberOfTransactions] = useState({});
  const [transactionVolume, setTransactionVolume] = useState({});

  const _fetchMarketOverview = async (pool, params, active) => {
    try {
      setMarketOverviewLoading(true);
      const _params = params;
      _params["lending"] = pool;
      const data = await monitoringClient.get("/overview/market-volatility", { params: _params });
      let totalSupply = [];
      let totalBorrow = [];
      let tvl = [];
      const _data = Object.entries(data.data);
      for (var item of _data) {
        const timestamp = item[0] * 1000;
        const _supply = item[1]["totalSupplyChangeLogs"];
        const _borrow = item[1]["totalBorrowChangeLogs"];
        const _tvl = item[1]["tvlChangeLogs"];
        const defaultConfig = {
          marker: {
            enabled: true,
            symbol: "circle",
            radius: 6,
          },
        };
        if (item[1]?.supply) totalSupply.push({ x: timestamp, y: _supply, ...defaultConfig });
        else totalSupply.push({ x: timestamp, y: _supply });
        if (item[1]?.borrow) totalBorrow.push({ x: timestamp, y: _borrow, ...defaultConfig });
        else totalBorrow.push({ x: timestamp, y: _borrow });
        if (item[1]?.tvl) tvl.push({ x: timestamp, y: _tvl, ...defaultConfig });
        else tvl.push({ x: timestamp, y: _tvl });
      }
      let _tempData = marketOverview;
      _tempData[pool] = { supply: totalSupply, borrow: totalBorrow, tvl: tvl, metadata: params, active };
      setMarketOverview({ ..._tempData });
    } catch (error) {
      console.error(error);
    } finally {
      setMarketOverviewLoading(false);
    }
  };

  const fetchMarketOverview = async (pool, params, config) => {
    if (marketOverview[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = marketOverview[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchMarketOverview(pool, params, config?.active);
      }
    } else await _fetchMarketOverview(pool, params, config?.active);
  };

  const _fetchTokenDomination = async (pool, params, active) => {
    try {
      setTokenDominationLoading(true);
      const _params = params;
      _params["lending"] = pool;
      const data = await monitoringClient.get("/overview/value-percentage-volatility-token", { params: _params });
      const _data = data.data;
      let timestamps = [];
      let dataArr = {};
      for (var item of _data) {
        const _temp = Object.entries(item)[0];
        const timestamp = _temp[0] * 1000;
        timestamps.push(timestamp);
        const tokenData = Object.entries(_temp[1]);
        for (var _token of tokenData) {
          if (dataArr[_token[0]]) dataArr[_token[0]].push(_token[1]);
          else dataArr[_token[0]] = [_token[1]];
        }
      }
      let _tempData = tokenDomination;
      _tempData[pool] = { timestamps, data: dataArr, metadata: params, active };
      setTokenDomination({ ..._tempData });
    } catch (error) {
      console.error(error);
    } finally {
      setTokenDominationLoading(false);
    }
  };

  const fetchTokenDomination = async (pool, params, config) => {
    if (tokenDomination[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = tokenDomination[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchTokenDomination(pool, params, config?.active);
      }
    } else await _fetchTokenDomination(pool, params, config?.active);
  };

  const _fetchUtilizationRate = async (pool, params, active) => {
    try {
      setUtilizationRateLoading(true);
      const _params = params;
      _params["lending"] = pool;
      _params["type"] = "utilization";
      let data = await monitoringClient.get("/overview/interest-rate", { params: _params });
      const _data = data.data;
      let dataArr = {};
      for (var item of _data) {
        const _temp = Object.entries(item)[0];
        const timestamp = _temp[0] * 1000;
        const tokenData = Object.entries(_temp[1]);
        for (var _token of tokenData) {
          if (dataArr[_token[0]]) dataArr[_token[0]].push([timestamp, _token[1]]);
          else dataArr[_token[0]] = [[timestamp, _token[1]]];
        }
      }
      let _tempData = utilizationRate;
      _tempData[pool] = { data: dataArr, metadata: params, active };
      setUtilizationRate({ ..._tempData });
    } catch (error) {
      console.error(error);
    } finally {
      setUtilizationRateLoading(false);
    }
  };

  const fetchUtilizationRate = async (pool, params, config) => {
    if (utilizationRate[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = utilizationRate[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchUtilizationRate(pool, params, config?.active);
      }
    } else await _fetchUtilizationRate(pool, params, config?.active);
  };

  const _fetchInterestRate = async (pool, params, active) => {
    try {
      setInterestRateLoading(true);
      const _params = params;
      _params["lending"] = pool;
      const data = await monitoringClient.get("/overview/interest-rate", { params: _params });
      const _data = data.data;
      let dataArr = {};
      for (var item of _data) {
        const _temp = Object.entries(item)[0];
        const timestamp = _temp[0] * 1000;
        const tokenData = Object.entries(_temp[1]);
        for (var _token of tokenData) {
          if (dataArr[_token[0]]) dataArr[_token[0]].push([timestamp, _token[1]]);
          else dataArr[_token[0]] = [[timestamp, _token[1]]];
        }
      }
      let _tempData = interestRate;
      _tempData[pool] = { data: dataArr, metadata: params, active };
      setInterestRate({ ..._tempData });
    } catch (error) {
      console.error(error);
    } finally {
      setInterestRateLoading(false);
    }
  };

  const fetchInterestRate = async (pool, params, config) => {
    if (interestRate[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = interestRate[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchInterestRate(pool, params, config?.active);
      }
    } else await _fetchInterestRate(pool, params, config?.active);
  };

  const _fetchNumberOfTransactions = async (pool, params, active) => {
    try {
      const _params = params;
      _params["lending"] = pool;
      const data = await monitoringClient.get("/overview/number-tx", { params: _params });
      const _data = Object.entries(data);
      let timestamps = [];
      let deposit = [];
      let borrow = [];
      let withdraw = [];
      let repay = [];
      let index = 0;
      for (var item of _data) {
        if (index === 0) timestamps.push(`-${item[0] * 1000}`);
        else timestamps.push(`${_data[index - 1][0] * 1000}-${item[0] * 1000}`);
        deposit.push(item[1]["deposit"]);
        borrow.push(item[1]["borrow"]);
        withdraw.push(item[1]["withdraw"]);
        repay.push(item[1]["repay"]);
        index++;
      }
      let _tempData = numberOfTransactions;
      _tempData[pool] = {
        timestamps,
        deposit,
        borrow,
        withdraw,
        repay,
        metadata: params,
        active,
      };
      setNumberOfTransactions({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchNumberOfTransactions = async (pool, params, config) => {
    if (numberOfTransactions[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = numberOfTransactions[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchNumberOfTransactions(pool, params, config?.active);
      }
    } else await _fetchNumberOfTransactions(pool, params, config?.active);
  };

  const _fetchTransactionVolume = async (pool, params, active) => {
    try {
      const _params = params;
      _params["lending"] = pool;
      const data = await monitoringClient.get("/overview/volume-tx", { params: _params });
      const _data = Object.entries(data);
      let timestamps = [];
      let deposit = [];
      let borrow = [];
      let withdraw = [];
      let repay = [];
      let index = 0;
      for (var item of _data) {
        if (index === 0) timestamps.push(`-${item[0] * 1000}`);
        else timestamps.push(`${_data[index - 1][0] * 1000}-${item[0] * 1000}`);
        deposit.push(item[1]["deposit"]);
        borrow.push(item[1]["borrow"]);
        withdraw.push(item[1]["withdraw"]);
        repay.push(item[1]["repay"]);
        index++;
      }
      let _tempData = transactionVolume;
      _tempData[pool] = {
        timestamps: timestamps,
        deposit: deposit,
        borrow: borrow,
        withdraw: withdraw,
        repay: repay,
        metadata: params,
        active,
      };
      setTransactionVolume({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTransactionVolume = async (pool, params, config) => {
    if (transactionVolume[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = transactionVolume[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchTransactionVolume(pool, params, config?.active);
      }
    } else await _fetchTransactionVolume(pool, params, config?.active);
  };

  const contextData = useMemo(() => {
    return {
      chart: {
        marketOverview,
        tokenDomination,
        utilizationRate,
        interestRate,
        numberOfTransactions,
        transactionVolume,
      },
      loading: {
        marketOverviewLoading,
        tokenDominationLoading,
        utilizationRateLoading,
        interestRateLoading,
      },
      fetch: {
        fetchMarketOverview,
        fetchTokenDomination,
        fetchUtilizationRate,
        fetchInterestRate,
        fetchNumberOfTransactions,
        fetchTransactionVolume,
      },
    };
  }, [
    marketOverview,
    tokenDomination,
    utilizationRate,
    interestRate,
    numberOfTransactions,
    transactionVolume,
    marketOverviewLoading,
    tokenDominationLoading,
    utilizationRateLoading,
    interestRateLoading,
  ]);

  return <AnalysisOverviewContext.Provider value={contextData}>{children}</AnalysisOverviewContext.Provider>;
};

export default AnalysisOverviewProvider;

export const useAnalysisOverviewContext = () => React.useContext(AnalysisOverviewContext);

export const useAnalysisOverviewSelector = (selectorFn) => {
  const result = useAnalysisOverviewContext();

  return useMemo(() => selectorFn(result), [result]);
};

export const useAnalysisOverviewChartSelector = (chartType, pool) => {
  const { chart } = useAnalysisOverviewContext();
  return useMemo(() => {
    return chart[chartType][pool];
  }, [chart, chartType, pool]);
};
